import { Controller } from "@hotwired/stimulus";
import { loadRevealSDK, initializeRevealAI, updateRevealAIToken, buildScientistTheme, renderInsecureContextError } from "../helpers/reveal_sdk_loader";
import { captureRevealError, sanitizeRevealSaveError } from "../helpers/reveal_sentry";
import { generateUuid } from "../helpers/uuid";

// Controller for creating new Reveal BI dashboards
// See: https://help.revealbi.io/web/creating-dashboards/
export default class extends Controller {
  static values = {
    serverUrl: String,
    dashboardId: String,
    token: String,
    refreshUrl: String,
    refreshIntervalMinutes: { type: Number, default: 55 },
    aiEnabled: { type: Boolean, default: false },
    datasources: { type: Array, default: [] }
  };

  async connect() {
    if (renderInsecureContextError(this.element)) return;

    try {
      await loadRevealSDK();
    } catch (error) {
      captureRevealError("sdk load", error, { dashboardId: this.dashboardIdValue });
      this.element.innerHTML = '<div class="alert alert-danger">Failed to load dashboard. Please try again later.</div>';
      return;
    }
    if (this.aiEnabledValue) {
      initializeRevealAI(this.serverUrlValue, () => this.tokenValue);
    }
    this.initializeNewDashboard();
    this.startTokenRefresh();
  }

  initializeNewDashboard() {
    // Reveal 2.x: the `$.ig` global is gone; every type now lives on `window.Reveal`.
    const R = window.Reveal;

    // Configure server URL, theme, and auth headers
    R.RevealSdkSettings.setBaseUrl(this.serverUrlValue);
    R.RevealSdkSettings.theme = buildScientistTheme();
    R.RevealSdkSettings.setAdditionalHeadersProvider(() => ({
      "Authorization": `Bearer ${this.tokenValue}`
    }));

    // Create a new empty dashboard
    this.element.innerHTML = ""; // Clear spinner
    this.revealView = new R.RevealView(this.element);
    const dashboard = new R.RVDashboard();
    this.revealView.dashboard = dashboard;

    // Configure for editing
    this.revealView.canEdit = true;
    this.revealView.canSaveAs = true;
    this.revealView.startInEditMode = true;
    // See reveal_bi_embed_controller: at the SDK default (true) saveFinished()
    // fires the SDK's own POST /DashboardFile and writes a duplicate version row.
    this.revealView.serverSideSave = false;

    // Always allow exports
    this.revealView.showExportToPDF = true;
    this.revealView.showExportToExcel = true;
    this.revealView.showExportToImage = true;
    this.revealView.showExportToCsv = true;

    // Configure available data sources for the "Add Data Source" dialog
    this.revealView.onDataSourcesRequested = (callback) => {
      this.configureDataSources(R, callback);
    };

    // SDK has no onSaveAs event: both Save and Save As arrive via onSave, discriminated by args.saveAs.
    this.revealView.onSave = (rv, args) => this.handleSave(rv, args);
  }

  configureDataSources(R, callback) {
    // Create the PostgreSQL data source (connection details are set server-side)
    const postgresDataSource = new R.RVPostgresDataSource();
    postgresDataSource.id = "scientist-data-warehouse";
    postgresDataSource.title = "Scientist Data Warehouse";

    // Build Excel data source items from server-provided datasource list
    const excelDatasources = (this.datasourcesValue || []).filter(ds => ds.type === "excel");
    const excelItems = excelDatasources.map(ds => {
      const localFile = new R.RVLocalFileDataSourceItem();
      localFile.uri = `local:/${ds.name}`;
      const excelItem = new R.RVExcelDataSourceItem(localFile);
      excelItem.title = ds.name.replace(/\.xlsx$/, "");
      return excelItem;
    });

    callback(new R.RevealDataSources([postgresDataSource], excelItems, false));
  }

  startTokenRefresh() {
    const intervalMs = this.refreshIntervalMinutesValue * 60 * 1000;
    this.refreshInterval = setInterval(() => this.refreshToken(), intervalMs);
  }

  async refreshToken() {
    try {
      const response = await fetch(this.refreshUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        credentials: "same-origin"
      });
      if (response.ok) {
        const data = await response.json();
        this.tokenValue = data.token;
        updateRevealAIToken(data.token);
      } else if (response.status === 401) {
        this.stopTokenRefresh();
        this.element.innerHTML = `
          <div class="alert alert-warning">
            Your session has expired. Please <a href="/login">log in again</a> to continue.
          </div>
        `;
      } else {
        console.error(`Reveal token refresh failed: HTTP ${response.status}`); // eslint-disable-line no-console
      }
    } catch (error) {
      // Will retry on next interval, but the token is stale until then.
      console.error("Reveal token refresh failed:", error); // eslint-disable-line no-console
    }
  }

  stopTokenRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  handleSave(rv, args) {
    // Before the first successful save there is nothing on the server to fork, so Save As behaves like a plain save.
    const isFork = Boolean(args.saveAs) && this.createdOnServer === true;
    const dashboardId = isFork ? generateUuid() : this.dashboardIdValue;
    const copiedFrom = isFork ? this.dashboardIdValue : null;
    this.saveDashboardToServer(dashboardId, args, { copiedFrom, isFork });
  }

  saveDashboardToServer(dashboardId, args, { copiedFrom = null, isFork = false } = {}) {
    const query = copiedFrom ? `?copied_from=${copiedFrom}` : "";

    // The name typed in the Save As dialog lives only on args.name; serializeWithNewName stamps it into the .rdash Title.
    const serialize = (callback) => {
      if (isFork && args.name) {
        args.serializeWithNewName(args.name, callback);
      } else {
        args.serialize(callback);
      }
    };

    serialize((bytes) => {
      fetch(`${this.serverUrlValue}/dashboards/${dashboardId}${query}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${this.tokenValue}`,
          "Content-Type": "application/octet-stream"
        },
        body: bytes
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`Save failed: ${response.status}`);
          }
          // saveFinished() synchronously runs the SDK's edit-mode teardown; a throw in there must not be reported as a failed save.
          try {
            args.saveFinished();
          } catch (finishError) {
            console.error("Reveal saveFinished() failed after a successful save:", finishError); // eslint-disable-line no-console
          }
          this.createdOnServer = true;
          if (isFork) {
            this.dashboardIdValue = dashboardId;
          }
          this.showToast(isFork ? "Dashboard copied" : "Dashboard saved successfully");
          this.uploadThumbnail(dashboardId);
        })
        .catch((error) => {
          const safeError = sanitizeRevealSaveError(error);
          console.error("Failed to save dashboard:", safeError); // eslint-disable-line no-console
          captureRevealError(isFork ? "dashboard fork" : "dashboard save", safeError, { dashboardId });
          this.showToast("Failed to save dashboard. Please try again.", true);
        });
    });
  }

  uploadThumbnail(dashboardId) {
    if (!this.revealView) return;

    this.revealView.toImage().then((image) => {
      // toImage() returns an <img> element, extract the src attribute
      const dataUrl = image?.src;
      
      if (!dataUrl || typeof dataUrl !== "string") {
        console.error("toImage returned unexpected result:", image); // eslint-disable-line no-console
        return;
      }
      fetch(`${this.serverUrlValue}/dashboards/${dashboardId}/thumbnail`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${this.tokenValue}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ thumbnail: dataUrl })
      }).catch((error) => {
        console.error("Failed to upload thumbnail:", error); // eslint-disable-line no-console
      });
    }).catch((error) => {
      console.error("Failed to capture thumbnail:", error); // eslint-disable-line no-console
    });
  }

  showToast(message, isError = false) {
    const toast = document.createElement("div");
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 24px;
      border-radius: 4px;
      color: white;
      background-color: ${isError ? "#dc3545" : "#28a745"};
      z-index: 9999;
      opacity: 1;
      transition: opacity 0.3s ease;
    `;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = "0";
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }

  disconnect() {
    this.stopTokenRefresh();
  }

}
